/**
 * Countdown Timer Component
 *
 * Handles real-time countdown functionality for countdown blocks
 * Features: configurable time units, expiration handling, and theme editor support
 */

class CountdownTimer {
  /**
   * @param {HTMLElement} element - The DOM element containing the countdown timer
   */
  constructor(element) {
    this.element = element;
    this.config = this.parseConfig();
    this.elements = this.cacheElements();
    this.state = {
      interval: /** @type {number | null} */ (null),
      isExpired: false,
      isInitialized: false
    };
    
    this.init();
  }

  /**
   * Parse configuration from data attributes
   * @returns {{
   *   targetDate: Date,
   *   expiredMessage: string,
   *   hideWhenExpired: boolean
   * }} Configuration object
   */
  parseConfig() {
    const element = this.element;
    const countdownTarget = element.dataset.countdownTarget;
    
    if (!countdownTarget) {
      throw new Error('Countdown target date is required');
    }
    
    return {
      targetDate: new Date(countdownTarget),
      expiredMessage: element.dataset.expiredMessage || 'This offer has expired',
      hideWhenExpired: this.parseBooleanAttribute(element.dataset.hideWhenExpired)
    };
  }
  
  /**
   * Parse boolean data attribute values
   * @param {string | undefined} value - The data attribute value
   * @returns {boolean} Parsed boolean value
   */
  parseBooleanAttribute(value) {
    if (value === undefined) return false; // Default to false if not set
    return value === 'true' || value === '1' || value === 'yes';
  }
  
  /**
   * Cache DOM elements for better performance
   * @returns {{
   *   unitsContainer: Element | null,
   *   expiredContainer: Element | null,
   *   units: {
   *     days: Element | null,
   *     hours: Element | null,
   *     minutes: Element | null,
   *     seconds: Element | null
   *   }
   * }} Cached DOM elements
   */
  cacheElements() {
    const element = this.element;

    return {
      unitsContainer: element.querySelector('.countdown-timer__units'),
      expiredContainer: element.querySelector('.countdown-timer__expired'),
      units: {
        days: element.querySelector('[data-unit="days"] .countdown-timer__number'),
        hours: element.querySelector('[data-unit="hours"] .countdown-timer__number'),
        minutes: element.querySelector('[data-unit="minutes"] .countdown-timer__number'),
        seconds: element.querySelector('[data-unit="seconds"] .countdown-timer__number')
      }
    };
  }

  /**
   * Initialize the countdown timer
   */
  init() {
    if (this.state.isInitialized) return;
    
    // Validate target date
    if (!this.isValidDate(this.config.targetDate)) {
      console.error('Invalid countdown target date:', this.config.targetDate);
      return;
    }
    
    // Apply typography settings
    this.applyTypographySettings();
    
    // Start countdown
    this.startCountdown();
    this.addActiveClass();
    
    this.state.isInitialized = true;
  }

  /**
   * Check if date is valid
   * @param {Date} date - Date to validate
   * @returns {boolean} True if date is valid
   */
  isValidDate(date) {
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Start the countdown interval
   */
  startCountdown() {
    this.updateCountdown();
    this.state.interval = setInterval(() => this.updateCountdown(), 1000);
  }

  /**
   * Add active class for styling
   */
  addActiveClass() {
    this.element.classList.add('countdown-timer--active');
  }

  /**
   * Apply typography settings from schema
   */
  applyTypographySettings() {
    const countdownBlock = this.element.closest('.countdown-block');
    if (!countdownBlock) return;

    // Get typography settings from data attributes or use defaults
    const desktopNumberSize = /** @type {HTMLElement} */ (countdownBlock).dataset.desktopNumberSize || 'var(--font-h3--size)';
    const mobileNumberSize = /** @type {HTMLElement} */ (countdownBlock).dataset.mobileNumberSize || 'var(--font-h4--size)';

    // Update CSS custom properties
    /** @type {HTMLElement} */ (countdownBlock).style.setProperty('--countdown-desktop-number-size', desktopNumberSize);
    /** @type {HTMLElement} */ (countdownBlock).style.setProperty('--countdown-mobile-number-size', mobileNumberSize);
  }

  /**
   * Update countdown display
   */
  updateCountdown() {
    const now = new Date().getTime();
    const distance = this.config.targetDate.getTime() - now;

    if (distance < 0) {
      this.handleExpiration();
      return;
    }

    const timeLeft = this.calculateTimeUnits(distance);
    this.updateDisplay(timeLeft);
  }

  /**
   * Calculate time units from milliseconds
   * @param {number} distance - Time distance in milliseconds
   * @returns {{
   *   days: number,
   *   hours: number,
   *   minutes: number,
   *   seconds: number
   * }} Object containing days, hours, minutes, and seconds
   */
  calculateTimeUnits(distance) {
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const millisecondsPerHour = 1000 * 60 * 60;
    const millisecondsPerMinute = 1000 * 60;

    return {
      days: Math.floor(distance / millisecondsPerDay),
      hours: Math.floor((distance % millisecondsPerDay) / millisecondsPerHour),
      minutes: Math.floor((distance % millisecondsPerHour) / millisecondsPerMinute),
      seconds: Math.floor((distance % millisecondsPerMinute) / 1000)
    };
  }

  /**
   * Update the display with calculated time units
   * @param {{
   *   days: number,
   *   hours: number,
   *   minutes: number,
   *   seconds: number
   * }} timeLeft - Object containing time units
   */
  updateDisplay(timeLeft) {
    const { units } = this.elements;
    
    // Update each unit if it exists
    if (units.days) {
      units.days.textContent = this.formatNumber(timeLeft.days);
    }
    
    if (units.hours) {
      units.hours.textContent = this.formatNumber(timeLeft.hours);
    }
    
    if (units.minutes) {
      units.minutes.textContent = this.formatNumber(timeLeft.minutes);
    }
    
    if (units.seconds) {
      units.seconds.textContent = this.formatNumber(timeLeft.seconds);
    }
  }

  /**
   * Format number with leading zero
   * @param {number} num - Number to format
   * @returns {string} Formatted number string
   */
  formatNumber(num) {
    return num.toString().padStart(2, '0');
  }

  /**
   * Handle countdown expiration
   */
  handleExpiration() {
    if (this.state.isExpired) return;

    this.state.isExpired = true;
    this.stopCountdown();
    this.updateExpiredState();
    this.dispatchExpiredEvent();
  }

  /**
   * Stop the countdown interval
   */
  stopCountdown() {
    if (this.state.interval) {
      clearInterval(this.state.interval);
      this.state.interval = null;
    }
  }

  /**
   * Update UI state when expired
   */
  updateExpiredState() {
    if (this.config.hideWhenExpired) {
      this.hideElement();
    } else {
      this.showExpiredMessage();
    }

    this.updateClasses();
  }

  /**
   * Hide the entire countdown element
   */
  hideElement() {
    /** @type {HTMLElement} */ (this.element).style.display = 'none';
  }

  /**
   * Show expired message
   */
  showExpiredMessage() {
    if (this.elements.unitsContainer) {
      /** @type {HTMLElement} */ (this.elements.unitsContainer).style.display = 'none';
    }

    if (this.elements.expiredContainer) {
      /** @type {HTMLElement} */ (this.elements.expiredContainer).style.display = 'block';
    }
  }

  /**
   * Update CSS classes for expired state
   */
  updateClasses() {
    this.element.classList.add('countdown-timer--expired');
    this.element.classList.remove('countdown-timer--active');
  }

  /**
   * Dispatch custom event for other scripts
   */
  dispatchExpiredEvent() {
    this.element.dispatchEvent(
      new CustomEvent('countdown:expired', {
        detail: { timer: this }
      })
    );
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.stopCountdown();
    
    this.state.isInitialized = false;
  }

  /**
   * Get current state
   * @returns {Object} Copy of current state
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Get configuration
   * @returns {Object} Copy of configuration
   */
  getConfig() {
    return { ...this.config };
  }
}

/**
 * Countdown Manager - handles multiple countdown instances
 */
class CountdownManager {
  constructor() {
    this.timers = new Map();
    this.init();
  }

  /**
   * Initialize countdown timers
   */
  init() {
    this.initExistingTimers();
    this.bindEvents();
  }

  /**
   * Initialize existing countdown elements
   */
  initExistingTimers() {
    const countdownElements = document.querySelectorAll('.countdown-timer[data-countdown-target]');

    countdownElements.forEach((element) => {
      this.createTimer(element);
    });
  }

  /**
   * Create a new countdown timer
   * @param {Element} element - DOM element for the countdown timer
   * @returns {CountdownTimer} Created timer instance
   */
  createTimer(element) {
    // Avoid double initialization
    if (/** @type {any} */ (element).countdownTimer) {
      return /** @type {any} */ (element).countdownTimer;
    }

    const timer = new CountdownTimer(/** @type {HTMLElement} */ (element));
    /** @type {any} */ (element).countdownTimer = timer;
    this.timers.set(element, timer);

    return timer;
  }

  /**
   * Destroy a countdown timer
   * @param {Element} element - DOM element for the countdown timer
   */
  destroyTimer(element) {
    if (/** @type {any} */ (element).countdownTimer) {
      /** @type {any} */ (element).countdownTimer.destroy();
      /** @type {any} */ (element).countdownTimer = null;
      this.timers.delete(element);
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Theme editor events
    document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this));
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect.bind(this));
    
    // DOM changes (for dynamic content)
    this.observeDOMChanges();
  }

  /**
   * Handle block selection in theme editor
   * @param {Event} event - Block select event
   */
  handleBlockSelect(event) {
    const target = event.target;
    if (target && target instanceof Element) {
      const countdownElement = target.querySelector('.countdown-timer[data-countdown-target]');
      if (countdownElement) {
        this.createTimer(countdownElement);
      }
    }
  }

  /**
   * Handle block deselection in theme editor
   * @param {Event} event - Block deselect event
   */
  handleBlockDeselect(event) {
    const target = event.target;
    if (target && target instanceof Element) {
      const countdownElement = target.querySelector('.countdown-timer[data-countdown-target]');
      if (countdownElement) {
        this.destroyTimer(countdownElement);
      }
    }
  }

  /**
   * Observe DOM changes for dynamic content
   */
  observeDOMChanges() {
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = /** @type {Element} */ (node);

              // Check if the added node is a countdown timer
              if (element.classList && element.classList.contains('countdown-timer')) {
                this.createTimer(element);
              }

              // Check for countdown timers within the added node
              const countdownElements =
                element.querySelectorAll && element.querySelectorAll('.countdown-timer[data-countdown-target]');

              if (countdownElements) {
                countdownElements.forEach((element) => this.createTimer(element));
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Get all active timers
   * @returns {Array<CountdownTimer>} Array of active timer instances
   */
  getTimers() {
    return Array.from(this.timers.values());
  }

  /**
   * Destroy all timers
   */
  destroyAll() {
    this.timers.forEach((timer, element) => {
      this.destroyTimer(element);
    });
  }
}

/**
 * Initialize countdown functionality
 * @returns {CountdownManager} Manager instance
 */
function initCountdownTimers() {
  // Create global manager instance
  if (!(/** @type {any} */ (window).countdownManager)) {
    /** @type {any} */ (window).countdownManager = new CountdownManager();
  }

  return /** @type {any} */ (window).countdownManager;
}

/**
 * Initialize on DOM ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCountdownTimers);
} else {
  initCountdownTimers();
}

/**
 * Export for manual initialization and external use
 */
/** @type {any} */ (window).CountdownTimer = CountdownTimer;
/** @type {any} */ (window).CountdownManager = CountdownManager;
/** @type {any} */ (window).initCountdownTimers = initCountdownTimers;